import { NetworkCenterMark } from "./NetworkCenterMark";
import { NetworkCenterRings } from "./NetworkCenterRings";
import { NetworkHoverTargets } from "./NetworkHoverTargets";
import { NODES } from "@/app/features/diagrams/functions/heroSystem/constants";
import { heroTransition } from "@/app/features/diagrams/functions/heroSystem/motion";

export type NetworkNodesProps = {
  activeNode: string;
  selectedNode: string;
  handleHoverStart: (nodeId: string) => void;
  handleHoverEnd: () => void;
  handleClickNode: (nodeId: string) => void;
};

export function NetworkNodes({
  activeNode,
  selectedNode,
  handleHoverStart,
  handleHoverEnd,
  handleClickNode,
}: NetworkNodesProps) {
  return (
    <>
      {/* 3. Graph Nodes and Labels */}
      <g className="nodes-layer">
        {NODES.map((node) => {
          const isCenter = node.type === "center";
          const isCap = node.type === "capability";
          const isInd = node.type === "industry";

          // Responsive: hide industry nodes and outer labels on mobile
          const groupClass = isInd ? "hidden md:block" : "";

          // Check active states
          const isSelected = selectedNode === node.id;
          const isHovered = activeNode === node.id;

          const lit = isHovered || isSelected;
          const motion = heroTransition(lit);

          // Hovered node takes precedence in styling
          const nodeFill = isCenter
            ? "var(--ga-paper)"
            : isHovered
              ? "var(--ga-moss)"
              : isSelected
                ? "var(--ga-signal)"
                : "var(--ga-paper)";

          const nodeStroke = isCenter
            ? "var(--ga-line)"
            : isHovered
              ? "var(--ga-moss)"
              : isSelected
                ? "var(--ga-signal)"
                : isCap
                  ? "var(--ga-moss)"
                  : "var(--ga-line)";

          const nodeStrokeWidth = isCenter
            ? 1.0
            : isHovered || isSelected
              ? 1.0
              : 0.75;

          return (
            <g key={node.id} className={groupClass}>
              {isCenter && <NetworkCenterRings x={node.x} y={node.y} />}

              {/* Outer pulsing ring for selected or hovered nodes (always mounted, transitions opacity) */}
              {!isCenter && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={isCap ? 7.5 : 6}
                  fill="none"
                  stroke={nodeStroke}
                  strokeWidth="0.4"
                  className={`${motion} ${lit ? "animate-[ga-node-pulse_2.2s_ease-in-out_infinite] opacity-100" : "opacity-0"}`}
                  style={{ transformOrigin: `${node.x}px ${node.y}px` }}
                />
              )}

              {/* Node visual core circle (scales slightly on active/hover) */}
              <circle
                cx={node.x}
                cy={node.y}
                r={isCenter ? 8.5 : isCap ? 4 : 3}
                fill={nodeFill}
                stroke={nodeStroke}
                strokeWidth={nodeStrokeWidth}
                className={`${motion} ${lit ? "scale-125" : "scale-100"}`}
                style={{ transformOrigin: `${node.x}px ${node.y}px` }}
                filter={isSelected && isCap ? "url(#glow)" : undefined}
              />

              {isCenter && <NetworkCenterMark x={node.x} y={node.y} />}

              {/* Node Label Text */}
              {!isCenter && (
                <>
                  {node.id === "media-entertainment" ? (
                    /* Special multi-line wrapping for Media / Entertainment */
                    <text
                      x={node.labelX}
                      y={node.labelY - 2}
                      textAnchor={node.align}
                      fontSize="3.5"
                      fontFamily="var(--font-mono)"
                      fontWeight={lit ? "600" : "500"}
                      className={`${motion} ${lit ? "fill-ga-ink opacity-100" : "fill-ga-ink/40"}`}
                    >
                      <tspan x={node.labelX} dy="0">
                        Media /
                      </tspan>
                      <tspan x={node.labelX} dy="4.2">
                        Entertainment
                      </tspan>
                    </text>
                  ) : isCap ? (
                    /* Capability multi-line labels */
                    <text
                      x={node.labelX}
                      y={
                        node.id === "intelligent-systems"
                          ? node.labelY
                          : node.labelY - 2
                      }
                      textAnchor={node.align}
                      fontSize="4.0"
                      fontFamily="var(--font-mono)"
                      fontWeight={lit ? "600" : "500"}
                      className={`${motion} ${lit ? "fill-ga-ink opacity-100" : "fill-ga-ink/60"}`}
                    >
                      {node.id === "intelligent-systems" && (
                        <tspan x={node.labelX} dy="0">
                          Intelligent Systems
                        </tspan>
                      )}
                      {node.id === "data-search-knowledge" && (
                        <>
                          <tspan x={node.labelX} dy="0">
                            Data, Search
                          </tspan>
                          <tspan x={node.labelX} dy="4.5">
                            & Knowledge
                          </tspan>
                        </>
                      )}
                      {node.id === "automation-operations" && (
                        <>
                          <tspan x={node.labelX} dy="0">
                            Automation
                          </tspan>
                          <tspan x={node.labelX} dy="4.5">
                            & Operations
                          </tspan>
                        </>
                      )}
                      {node.id === "software-architecture-engineering" && (
                        <>
                          <tspan x={node.labelX} dy="0">
                            Software Architecture
                          </tspan>
                          <tspan x={node.labelX} dy="4.5">
                            & Engineering
                          </tspan>
                        </>
                      )}
                    </text>
                  ) : (
                    /* Industry single line labels */
                    <text
                      x={node.labelX}
                      y={node.labelY}
                      textAnchor={node.align}
                      fontSize="3.5"
                      fontFamily="var(--font-mono)"
                      fontWeight={lit ? "600" : "500"}
                      className={`${motion} ${lit ? "fill-ga-ink opacity-100" : "fill-ga-ink/40"}`}
                    >
                      {node.label}
                    </text>
                  )}
                </>
              )}
            </g>
          );
        })}
      </g>

      <NetworkHoverTargets
        handleHoverStart={handleHoverStart}
        handleHoverEnd={handleHoverEnd}
        handleClickNode={handleClickNode}
      />
    </>
  );
}
